/**
 * Pure resolver for the Sanity client configuration.
 *
 * The preview deployment (preview.agencyq.com) reads the `drafts` perspective so
 * editors can review unpublished content; production (www.agencyq.com) reads only
 * published content and never receives a token, so a draft can never leak.
 *
 * Kept as a pure function so the branch selection is unit-testable without
 * touching the network or the real Sanity client.
 */

export interface SanityClientEnv {
  projectId: string
  dataset: string
  apiVersion: string
  /** CDN setting for the published (production) path. Ignored in preview (drafts require useCdn: false). */
  useCdn: boolean
  /** When true, read drafts perspective with a token. Set only on the preview deployment. */
  previewDrafts: boolean
  /** Sanity read token with draft access. Required when previewDrafts is true. */
  readToken?: string
}

export interface ResolvedSanityClientConfig {
  projectId: string
  dataset: string
  apiVersion: string
  useCdn: boolean
  perspective?: 'drafts'
  token?: string
}

export function resolveSanityClientConfig(
  env: SanityClientEnv,
): ResolvedSanityClientConfig {
  const { projectId, dataset, apiVersion } = env

  if (env.previewDrafts) {
    if (!env.readToken) {
      throw new Error(
        'SANITY_PREVIEW_DRAFTS is enabled but SANITY_API_TOKEN is missing — a token with draft read access is required to read the drafts perspective.',
      )
    }
    return {
      projectId,
      dataset,
      apiVersion,
      useCdn: false,
      perspective: 'drafts',
      token: env.readToken,
    }
  }

  return { projectId, dataset, apiVersion, useCdn: env.useCdn }
}
